import React from 'react';
import { Calendar, User, Building, AlertCircle, FileText, FolderOpen } from 'lucide-react';
import { Badge } from './ui';
import { formatDate } from '../utils/dateFormatting';

const ProjectSetupCard = ({ project, onUpdate, onClick }) => {

  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--dark-elevated)',
        border: '1px solid var(--gray-600)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-4)',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all var(--transition-fast)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
      onMouseEnter={(e) => {
        if (onClick) {
          e.currentTarget.style.borderColor = 'var(--gray-500)';
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = 'var(--shadow-md)';
        }
      }}
      onMouseLeave={(e) => {
        if (onClick) {
          e.currentTarget.style.borderColor = 'var(--gray-600)';
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }
      }}
    >
      {/* Card Header */}
      <div
        style={{
          marginBottom: 'var(--space-3)',
          paddingBottom: 'var(--space-3)',
          borderBottom: '1px solid var(--gray-700)',
        }}
      >
        <div
          style={{
            marginBottom: 'var(--space-2)',
          }}
        >
          <h3
            style={{
              fontSize: 'var(--text-lg)',
              fontWeight: 'var(--font-semibold)',
              color: 'var(--gray-50)',
              margin: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              lineHeight: 1.3,
            }}
            title={`${project.jobNumber ? `${project.jobNumber}${project.rev ? ` Rev${project.rev}` : ''}` : 'No Job #'} - ${project.projectName || 'Unnamed Project'}`}
          >
            {project.jobNumber ? (
              <>
                <span style={{ color: 'var(--gray-400)' }}>
                  {project.jobNumber}{project.rev ? ` Rev${project.rev}` : ''}
                </span>
                {' - '}
                {project.projectName || 'Unnamed Project'}
              </>
            ) : (
              project.projectName || 'Unnamed Project'
            )}
          </h3>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 'var(--space-3)',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-1)',
                padding: '4px 8px',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--text-xs)',
                fontWeight: 'var(--font-medium)',
                color: project.pdfsReceived ? 'var(--success)' : 'var(--gray-500)',
                backgroundColor: project.pdfsReceived ? 'rgba(34, 197, 94, 0.1)' : 'var(--gray-800)',
                border: project.pdfsReceived ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid var(--gray-700)',
              }}
              title={project.pdfsReceived ? "PDFs Received" : "PDFs Not Received"}
            >
              <FileText size={12} />
              <span>PDFs</span>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-1)',
                padding: '4px 8px',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--text-xs)',
                fontWeight: 'var(--font-medium)',
                color: project.cadFilesReceived ? 'var(--success)' : 'var(--gray-500)',
                backgroundColor: project.cadFilesReceived ? 'rgba(34, 197, 94, 0.1)' : 'var(--gray-800)',
                border: project.cadFilesReceived ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid var(--gray-700)',
              }}
              title={project.cadFilesReceived ? "CAD Files Received" : "CAD Files Not Received"}
            >
              <FolderOpen size={12} />
              <span>CAD</span>
            </div>
          </div>
          {project.modelingState && (
            <Badge
              variant={project.modelingState === 'Hold' ? 'warning' : 'secondary'}
              size="sm"
            >
              {project.modelingState}
            </Badge>
          )}
        </div>
      </div>

      {/* Card Body */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-3)',
        }}
      >
        {project.clientName && (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--space-3)',
            }}
          >
            <Building
              size={16}
              style={{
                flexShrink: 0,
                marginTop: '2px',
                color: 'var(--gray-400)',
              }}
            />
            <div style={{ display: 'flex', gap: 'var(--space-2)', minWidth: 0, flex: 1 }}>
              <span
                style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--gray-400)',
                  fontWeight: 'var(--font-medium)',
                  flexShrink: 0,
                }}
              >
                Client:
              </span>
              <span
                style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--gray-300)',
                  wordBreak: 'break-word',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={project.clientName}
              >
                {project.clientName}
              </span>
            </div>
          </div>
        )}

        {project.modelBuilderEmployee?.name && (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--space-3)',
            }}
          >
            <User
              size={16}
              style={{
                flexShrink: 0,
                marginTop: '2px',
                color: 'var(--gray-400)',
              }}
            />
            <div style={{ display: 'flex', gap: 'var(--space-2)', minWidth: 0, flex: 1 }}>
              <span
                style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--gray-400)',
                  fontWeight: 'var(--font-medium)',
                  flexShrink: 0,
                }}
              >
                Builder:
              </span>
              <span
                style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--gray-300)',
                  wordBreak: 'break-word',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={project.modelBuilderEmployee.name}
              >
                {project.modelBuilderEmployee.name}
              </span>
            </div>
          </div>
        )}

        {project.dueDate && (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--space-3)',
            }}
          >
            <Calendar
              size={16}
              style={{
                flexShrink: 0,
                marginTop: '2px',
                color: 'var(--gray-400)',
              }}
            />
            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
              <span
                style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--gray-400)',
                  fontWeight: 'var(--font-medium)',
                }}
              >
                Due:
              </span>
              <span
                style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--gray-300)',
                }}
              >
                {formatDate(project.dueDate)}
              </span>
            </div>
          </div>
        )}

        {project.notes && (
          <div
            style={{
              marginTop: 'var(--space-2)',
              padding: 'var(--space-3)',
              backgroundColor: 'var(--dark-surface)',
              borderRadius: 'var(--radius-md)',
              display: 'flex',
              gap: 'var(--space-2)',
            }}
          >
            <AlertCircle
              size={14}
              style={{
                flexShrink: 0,
                marginTop: '2px',
                color: 'var(--gray-400)',
              }}
            />
            <p
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-400)',
                margin: 0,
                lineHeight: 1.4,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
              }}
              title={project.notes}
            >
              {project.notes.length > 100 ? `${project.notes.substring(0, 100)}...` : project.notes}
            </p>
          </div>
        )}
      </div>

      {/* Card Footer */}
      {project.createdAt && (
        <div
          style={{
            marginTop: 'auto',
            paddingTop: 'var(--space-3)',
            borderTop: '1px solid var(--gray-700)',
          }}
        >
          <span
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--gray-500)',
            }}
          >
            Created: {formatDate(project.createdAt)}
          </span>
        </div>
      )}
    </div>
  );
};

export default ProjectSetupCard;